import React, { useState, useEffect } from 'react';
import { X, Send, Mail, MessageSquare, CheckCircle, Package, ChevronDown, AlertCircle, Users } from 'lucide-react';
import { useQuery, useMutation } from '@apollo/client';
import { GET_AGENCY_PACKAGES } from '../../graphql/admin/queries/package';
import { SEND_OFFER_TO_CLIENT } from '../../graphql/admin/mutations/client';
import { SEND_WHATSAPP_OFFER, SEND_WHATSAPP_BULK } from '../../graphql/mutations/whatsapp';
import type { WhatsAppAccount, WhatsAppTemplate } from '../../types/whatsapp';

interface Client {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
}

interface AgencyPackage {
  id: number;
  title: string;
  image?: string;
  departure?: string;
  departures?: { departureDate?: string }[];
}

interface SendOfferModalProps {
  isOpen: boolean;
  onClose: () => void;
  clients: Client[];
  agencyName: string;
  waAccount: WhatsAppAccount | null;
  templates: WhatsAppTemplate[];
  onSent?: () => void;
}

type Channel = 'email' | 'whatsapp' | 'both';

export default function SendOfferModal({
  isOpen, onClose, clients, agencyName, waAccount, templates, onSent
}: SendOfferModalProps) {
  const isBulk = clients.length > 1;
  const singleClient = clients.length === 1 ? clients[0] : null;

  const [selectedPkg, setSelectedPkg] = useState<AgencyPackage | null>(null);
  const [showPkgDropdown, setShowPkgDropdown] = useState(false);
  const [pkgSearch, setPkgSearch] = useState('');
  const [channel, setChannel] = useState<Channel>('email');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailMessage, setEmailMessage] = useState('');
  const [waMessage, setWaMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<number | null>(null);
  const [results, setResults] = useState<{ label: string; success: boolean; error?: string }[]>([]);
  const [error, setError] = useState('');

  const { data: pkgsData, loading: pkgsLoading } = useQuery(GET_AGENCY_PACKAGES, {
    variables: { agencyName },
    skip: !agencyName || !isOpen,
  });

  const packages: AgencyPackage[] = pkgsData?.agencyPackages ?? [];
  const filtered = packages.filter(p =>
    p.title.toLowerCase().includes(pkgSearch.toLowerCase()) ||
    (p.departure || '').toLowerCase().includes(pkgSearch.toLowerCase())
  );

  const waConnected = waAccount?.isActive === true;
  const needsEmail = channel === 'email' || channel === 'both';
  const needsWa = channel === 'whatsapp' || channel === 'both';

  const clientsWithEmail = clients.filter(c => c.email);
  const clientsWithPhone = clients.filter(c => c.phone);

  useEffect(() => {
    if (!isOpen) {
      setSelectedPkg(null);
      setChannel('email');
      setEmailSubject('');
      setEmailMessage('');
      setWaMessage('');
      setResults([]);
      setError('');
      setSelectedTemplate(null);
      setPkgSearch('');
    }
  }, [isOpen]);

  useEffect(() => {
    if (selectedPkg) {
      const name = singleClient ? singleClient.firstName : 'chers clients';
      const dept = selectedPkg.departures?.[0]?.departureDate
        ? new Date(selectedPkg.departures[0].departureDate).toLocaleDateString('fr-FR')
        : '';
      setEmailSubject(`Offre de voyage — ${selectedPkg.title}`);
      setEmailMessage(
        `Bonjour ${name},\n\nNous avons le plaisir de vous proposer notre voyage "${selectedPkg.title}"${selectedPkg.departure ? ` vers ${selectedPkg.departure}` : ''}${dept ? `, départ le ${dept}` : ''}.\n\nN'hésitez pas à nous contacter pour plus d'informations.\n\nCordialement`
      );
    }
  }, [selectedPkg, singleClient]);

  const [sendEmail, { loading: sendingEmail }] = useMutation(SEND_OFFER_TO_CLIENT, {
    onCompleted: (data) => {
      const ok = data.sendOfferToClient?.success;
      setResults(prev => [...prev, { label: 'Email', success: !!ok, error: ok ? undefined : data.sendOfferToClient?.message }]);
    },
    onError: (err) => setResults(prev => [...prev, { label: 'Email', success: false, error: err.message }]),
  });

  const [sendWaSingle, { loading: sendingWaSingle }] = useMutation(SEND_WHATSAPP_OFFER, {
    onCompleted: (data) => {
      const ok = data.sendWhatsAppOffer?.success;
      setResults(prev => [...prev, { label: `WhatsApp — ${singleClient?.firstName}`, success: !!ok, error: ok ? undefined : data.sendWhatsAppOffer?.error }]);
    },
    onError: (err) => setResults(prev => [...prev, { label: 'WhatsApp', success: false, error: err.message }]),
  });

  const [sendWaBulk, { loading: sendingWaBulk }] = useMutation(SEND_WHATSAPP_BULK, {
    onCompleted: (data) => {
      const res: { success: boolean; error?: string }[] = data.sendWhatsAppBulk ?? [];
      const withPhone = clientsWithPhone;
      res.forEach((r, i) => {
        const c = withPhone[i];
        setResults(prev => [...prev, {
          label: `WhatsApp — ${c ? `${c.firstName} ${c.lastName}` : `#${i + 1}`}`,
          success: r.success,
          error: r.error,
        }]);
      });
    },
    onError: (err) => setResults(prev => [...prev, { label: 'WhatsApp (groupé)', success: false, error: err.message }]),
  });

  const loading = sendingEmail || sendingWaSingle || sendingWaBulk;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setResults([]);

    if (!selectedPkg) { setError('Sélectionnez une offre'); return; }
    if (clients.length === 0) { setError('Aucun client sélectionné'); return; }
    if (needsWa && !waConnected) { setError('WhatsApp non connecté — connectez-le dans l\'onglet Vue d\'ensemble'); return; }
    if (needsEmail && clientsWithEmail.length === 0) { setError('Aucun client n\'a d\'adresse email'); return; }
    if (needsWa && clientsWithPhone.length === 0) { setError('Aucun client n\'a de numéro de téléphone'); return; }

    const dept = selectedPkg.departures?.[0]?.departureDate
      ? new Date(selectedPkg.departures[0].departureDate).toLocaleDateString('fr-FR')
      : undefined;

    const sends: Promise<void>[] = [];

    if (needsEmail) {
      sends.push(
        sendEmail({
          variables: {
            packageId: selectedPkg.id,
            objet: emailSubject || `Offre — ${selectedPkg.title}`,
            messagePersonnalise: emailMessage,
            clientsEmails: clientsWithEmail.map(c => c.email),
          },
        }).then(() => {})
      );
    }

    if (needsWa) {
      if (isBulk) {
        sends.push(
          sendWaBulk({
            variables: {
              input: {
                recipients: clientsWithPhone.map(c => ({ phone: c.phone, name: `${c.firstName} ${c.lastName}` })),
                packageId: selectedPkg.id,
                title: selectedPkg.title,
                destination: selectedPkg.departure || selectedPkg.title,
                departureDate: dept,
                imageUrl: selectedPkg.image || undefined,
              },
            },
          }).then(() => {})
        );
      } else {
        sends.push(
          sendWaSingle({
            variables: {
              input: {
                recipientPhone: singleClient!.phone,
                recipientName: `${singleClient!.firstName} ${singleClient!.lastName}`,
                packageId: selectedPkg.id,
                title: selectedPkg.title,
                destination: selectedPkg.departure || selectedPkg.title,
                departureDate: dept,
                imageUrl: selectedPkg.image || undefined,
                customMessage: waMessage || undefined,
              },
            },
          }).then(() => {})
        );
      }
    }

    await Promise.all(sends);
    onSent?.();
  };

  if (!isOpen || clients.length === 0) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl w-full max-w-2xl shadow-xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Envoyer une offre</h2>
            <p className="text-sm text-gray-500 mt-0.5">
              {isBulk ? (
                <span className="flex items-center gap-1.5">
                  <Users className="w-3.5 h-3.5" />
                  {clients.length} clients sélectionnés
                </span>
              ) : (
                <>à <span className="font-medium text-gray-700">{singleClient?.firstName} {singleClient?.lastName}</span></>
              )}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X className="w-5 h-5" /></button>
        </div>

        {/* Liste clients (bulk) */}
        {isBulk && (
          <div className="px-5 pt-4 pb-0">
            <div className="flex flex-wrap gap-1.5 max-h-20 overflow-y-auto">
              {clients.map(c => (
                <span key={c.id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-50 text-blue-700 rounded-full text-xs font-medium">
                  <div className="w-4 h-4 bg-blue-200 rounded-full flex items-center justify-center text-blue-700 text-xs font-bold flex-shrink-0">
                    {c.firstName[0]?.toUpperCase()}
                  </div>
                  {c.firstName} {c.lastName}
                  {!c.email && !c.phone && <span className="text-orange-400 ml-0.5">⚠</span>}
                </span>
              ))}
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto p-5 space-y-5">
          {/* Résultats */}
          {results.length > 0 && (
            <div className="space-y-1.5">
              {results.map((r, i) => (
                <div key={i} className={`flex items-center gap-2 text-sm rounded-lg px-3 py-2 ${r.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                  {r.success ? <CheckCircle className="w-4 h-4 flex-shrink-0" /> : <AlertCircle className="w-4 h-4 flex-shrink-0" />}
                  <span>{r.label} : {r.success ? 'Envoyé' : r.error}</span>
                </div>
              ))}
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm bg-red-50 text-red-700 border border-red-200 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 flex-shrink-0" /> {error}
            </div>
          )}

          {/* Sélection offre */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Offre <span className="text-red-500">*</span></label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowPkgDropdown(v => !v)}
                className="w-full flex items-center gap-3 px-3 py-2.5 border border-gray-300 rounded-lg text-sm text-left hover:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {selectedPkg ? (
                  <>
                    {selectedPkg.image && <img src={selectedPkg.image} alt="" className="w-8 h-8 object-cover rounded flex-shrink-0" />}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-gray-900 truncate">{selectedPkg.title}</p>
                      {selectedPkg.departure && <p className="text-xs text-gray-500">{selectedPkg.departure}</p>}
                    </div>
                  </>
                ) : (
                  <span className="flex items-center gap-2 text-gray-400">
                    <Package className="w-4 h-4" />
                    {pkgsLoading ? 'Chargement...' : 'Sélectionner une offre'}
                  </span>
                )}
                <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 ml-auto" />
              </button>

              {showPkgDropdown && (
                <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg overflow-hidden">
                  <div className="p-2 border-b border-gray-100">
                    <input
                      autoFocus
                      value={pkgSearch}
                      onChange={e => setPkgSearch(e.target.value)}
                      placeholder="Rechercher une offre..."
                      className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      onClick={e => e.stopPropagation()}
                    />
                  </div>
                  <div className="max-h-52 overflow-y-auto">
                    {filtered.length === 0 ? (
                      <p className="text-center text-sm text-gray-400 py-4">Aucune offre trouvée</p>
                    ) : (
                      filtered.map(pkg => (
                        <button
                          key={pkg.id}
                          type="button"
                          onClick={() => { setSelectedPkg(pkg); setShowPkgDropdown(false); setPkgSearch(''); }}
                          className="w-full flex items-center gap-3 px-3 py-2.5 hover:bg-gray-50 text-left"
                        >
                          {pkg.image && <img src={pkg.image} alt="" className="w-8 h-8 object-cover rounded flex-shrink-0" />}
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{pkg.title}</p>
                            {pkg.departure && <p className="text-xs text-gray-500">{pkg.departure}</p>}
                          </div>
                        </button>
                      ))
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Canal */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Canal d'envoi</label>
            <div className="grid grid-cols-3 gap-2">
              {([
                { value: 'email',    label: 'Email',      icon: <Mail className="w-4 h-4" />,            disabled: clientsWithEmail.length === 0 },
                { value: 'whatsapp', label: 'WhatsApp',   icon: <MessageSquare className="w-4 h-4" />,   disabled: clientsWithPhone.length === 0 },
                { value: 'both',     label: 'Les deux',   icon: <Send className="w-4 h-4" />,            disabled: clientsWithEmail.length === 0 || clientsWithPhone.length === 0 },
              ] as { value: Channel; label: string; icon: React.ReactNode; disabled: boolean }[]).map(opt => (
                <button
                  key={opt.value}
                  type="button"
                  disabled={opt.disabled}
                  onClick={() => setChannel(opt.value)}
                  className={`flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg border text-sm font-medium transition-colors disabled:opacity-40 disabled:cursor-not-allowed ${
                    channel === opt.value ? 'bg-blue-600 border-blue-600 text-white' : 'border-gray-300 text-gray-700 hover:border-blue-400'
                  }`}
                >
                  {opt.icon} {opt.label}
                </button>
              ))}
            </div>
            {needsWa && !waConnected && (
              <p className="mt-2 text-xs text-amber-600 flex items-center gap-1">
                <AlertCircle className="w-3.5 h-3.5" />
                WhatsApp non connecté — allez dans l'onglet Vue d'ensemble
              </p>
            )}
            {needsEmail && isBulk && clientsWithEmail.length < clients.length && (
              <p className="mt-1.5 text-xs text-gray-500">
                {clientsWithEmail.length}/{clients.length} clients ont un email
              </p>
            )}
            {needsWa && isBulk && clientsWithPhone.length < clients.length && (
              <p className="mt-1.5 text-xs text-gray-500">
                {clientsWithPhone.length}/{clients.length} clients ont un téléphone
              </p>
            )}
          </div>

          {/* Champs Email */}
          {needsEmail && (
            <div className="space-y-3 border border-blue-100 bg-blue-50/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-blue-700">
                <Mail className="w-4 h-4" />
                {isBulk ? `Email — ${clientsWithEmail.length} destinataire${clientsWithEmail.length > 1 ? 's' : ''}` : `Email — ${singleClient?.email}`}
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Objet</label>
                <input
                  value={emailSubject}
                  onChange={e => setEmailSubject(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Message</label>
                <textarea
                  value={emailMessage}
                  onChange={e => setEmailMessage(e.target.value)}
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                />
              </div>
            </div>
          )}

          {/* Champs WhatsApp */}
          {needsWa && (
            <div className="space-y-3 border border-green-100 bg-green-50/30 rounded-lg p-4">
              <div className="flex items-center gap-2 text-sm font-medium text-green-700">
                <MessageSquare className="w-4 h-4" />
                {isBulk ? `WhatsApp — ${clientsWithPhone.length} destinataire${clientsWithPhone.length > 1 ? 's' : ''}` : `WhatsApp — ${singleClient?.phone}`}
              </div>
              {!isBulk && (
                <>
                  {templates.length > 0 && (
                    <div className="flex gap-2 flex-wrap">
                      {templates.map(t => (
                        <button key={t.id} type="button"
                          onClick={() => { setWaMessage(t.content); setSelectedTemplate(t.id); }}
                          className={`text-xs px-2 py-1 rounded-full border transition-colors ${selectedTemplate === t.id ? 'bg-green-100 border-green-400 text-green-700' : 'border-gray-300 text-gray-600 hover:border-green-400'}`}
                        >
                          {t.name}
                        </button>
                      ))}
                    </div>
                  )}
                  <textarea
                    value={waMessage}
                    onChange={e => { setWaMessage(e.target.value); setSelectedTemplate(null); }}
                    rows={3}
                    placeholder="Message personnalisé (optionnel — format auto si vide)..."
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-green-500 resize-none"
                  />
                </>
              )}
              {isBulk && (
                <p className="text-xs text-gray-500">Le message sera généré automatiquement depuis les infos de l'offre.</p>
              )}
            </div>
          )}
        </form>

        {/* Footer */}
        <div className="flex gap-3 p-5 border-t border-gray-200">
          <button type="button" onClick={onClose}
            className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-50">
            Annuler
          </button>
          <button
            onClick={handleSubmit}
            disabled={loading || !selectedPkg || (needsWa && !waConnected)}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {loading
              ? <><span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" /> Envoi...</>
              : <><Send className="w-4 h-4" /> {isBulk ? `Envoyer à ${clients.length} clients` : 'Envoyer'}</>
            }
          </button>
        </div>
      </div>
    </div>
  );
}
